import { Component, effect, inject, signal } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { BinderAccess } from 'src/app/models/binder-access.model';
import { ChipModule } from 'primeng/chip';
import { CommonModule } from '@angular/common';
import { BinderService } from '@/services/api/binder.service';
import { UsersService } from '@/services/api/users.service';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ShareService } from '@/services/api/share.service';
import { UserData } from '@/models/user-data.model';
import { DirectoryService } from '@/services/api/directory.service';
import { AvatarModule } from 'primeng/avatar';
import { UserTagComponent } from '@/global/user-tag.component';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { MultiSelectModule } from 'primeng/multiselect';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { UserAccessService } from '@/services/app/user-access.service';
import { AppAuthService } from '@/services/app/auth.service';
import { ConfirmPopupModule } from 'primeng/confirmpopup';

@Component({
  selector: 'app-binder-share',
  imports: [
    ToastModule,
    InputTextModule,
    ButtonModule,
    InputGroupModule,
    ChipModule,
    CommonModule,
    InputGroupAddonModule,
    AvatarModule,
    UserTagComponent,
    AvatarGroupModule,
    MultiSelectModule,
    FormsModule,
    SelectModule,
    ConfirmPopupModule
  ],
  template: `
<div class="flex flex-col gap-4 min-w-[35rem]">
  <div>
    <span class="font-medium text-surface-900 dark:text-surface-0 block mb-2"
      >Link to this binder</span
    >
    <p-inputgroup>
      <input pInputText [value]="binderUrl" readonly class="w-[35rem]" />
      <p-inputgroup-addon>
        <button
          pButton
          icon="pi pi-copy"
          [severity]="'secondary'"
          (click)="copyUrlToClipboard()"
        ></button>
      </p-inputgroup-addon>
    </p-inputgroup>
  </div>
  <div>
    <span
      class="font-medium text-surface-900 dark:text-surface-0 block mb-2 mt-0"
      >Team Members</span
    >
    <div class="flex items-center gap-2">
      @for(member of teamMembers; track member) {
      <app-user-tag [user]="member" [showLabel]="false" />
      }
    </div>
  </div>
  <div>
    <span class="font-medium text-surface-900 dark:text-surface-0 block mb-2"
      >Invite to this binder</span
    >
    <div class="flex">
      <p-inputgroup>
        <p-select
          appendTo="body"
          [disabled]="!shareUnlocked() || shareService.shareLoading()"
          [options]="userList()"
          [(ngModel)]="shareUser"
          optionLabel="DisplayName"
          [filter]="true"
          filterBy="DisplayName"
          placeholder="Find A User"
        >
          <ng-template #selectedItem let-selected>
            <div class="flex items-center gap-2">
              <p-avatar
                shape="circle"
                *ngIf="getUserImage(shareUser)"
                size="normal"
                [image]="getUserImage(shareUser) || ''"
                class="avatar-align"
              />
              <p-avatar
                shape="circle"
                *ngIf="!getUserImage(shareUser)"
                [label]="getInitials(shareUser?.DisplayName || '')"
                size="normal"
                class="avatar-align text-avatar"
              />
              <div>{{ selected.DisplayName }}</div>
            </div>
          </ng-template>
          <ng-template let-user #item>
            <div class="flex items-center gap-2">
              <p-avatar
                shape="circle"
                *ngIf="getUserImage(user)"
                size="normal"
                [image]="getUserImage(user) || ''"
                class="avatar-align"
              />
              <p-avatar
                shape="circle"
                *ngIf="!getUserImage(user)"
                [label]="getInitials(user?.DisplayName || '')"
                size="normal"
                class="avatar-align text-avatar"
              />
              <div>{{ user.DisplayName }}</div>
            </div>
          </ng-template>
        </p-select>
        <p-select
          size="large"
          [options]="accessOptions"
          [(ngModel)]="selectedAccess"
          appendTo="body"
          [disabled]="!shareUnlocked() || shareService.shareLoading()"
        />
        <button
          pButton
          label="Invite"
          [icon]="shareService.shareLoading() ? 'pi pi-spinner pi-spin' : 'pi pi-users'"
          (click)="onInvite()"
          [disabled]="!shareUser || shareService.shareLoading() || !shareUnlocked()"
        ></button>
      </p-inputgroup>
    </div>
  </div>
  <div>
    <ul class="list-none p-0 m-0 flex flex-col gap-4">
      @for(member of shared; track member) {
      <li class="flex items-center gap-2">
        <p-avatar
          shape="circle"
          *ngIf="getUserImage(member)"
          size="normal"
          [image]="getUserImage(member) || ''"
          class="avatar-align"
        />
        <p-avatar
          shape="circle"
          *ngIf="!getUserImage(member)"
          [label]="getInitials(member.Users.DisplayName || '')"
          size="normal"
          class="avatar-align text-avatar"
        />
        <div>
          <span class="font-medium">{{ member.Users.DisplayName }}</span>
          <!-- <div class="text-sm text-muted-color">{{ member.Users.User }}</div> -->
        </div>
        <div class="flex items-center gap-2 text-muted-color ml-auto text-sm">
          <span>{{ getPerms(member) }}</span>
          <!-- <i class="pi pi-times"></i> -->
          <p-button
            [icon]="shareService.removeLoading() ? 'pi pi-spinner pi-spin' : 'pi pi-times'"
            size="small"
            class="p-0"
            [text]="true"
            severity="danger"
            (click)="confirmRemove(member)"
          />
        </div>
      </li>
      }
    </ul>
  </div>
</div>

        <!-- <p-confirmpopup /> -->
  `
})
export class BinderShareComponent {
  binderUrl: string = '';
  addAccess: BinderAccess[] = [];
  removeAccess: BinderAccess[] = [];
  accessOptions: string[] = ['View Only', 'Edit Access'];
  selectedAccess: string = 'Edit Access';
  shareUser: UserData | undefined;

  userList = signal<UserData[]>([]);

  //services
  private appAuthService = inject(AppAuthService)
  private confirmationService = inject(ConfirmationService)
  private currentBinderService = inject(BinderService)
  public usersService = inject(UsersService)
  private directoryService = inject(DirectoryService)
  private messageService = inject(MessageService)
  public shareService = inject(ShareService)

  constructor(
  ) {
    effect(() => {
      const binder = this.currentBinderService.currentBinder();
      if (binder) {
        this.binderUrl = 'https://lexar.app/app/binders/edit/' + binder.BinderID;
        this.shareService.getSharedData(binder.BinderID, binder.SelectedFID);
      }
    })
    effect(() => {
      const team = this.shareService.teamData();
      const shared = this.shareService.sharedData();
      const users = this.usersService.userList();
      if (users && shared && team) {
        this.userList.set(users.filter((user) => !shared.some((member) => member.UID === user.UID) && !team.some((member) => member.UID === user.UID) && user.DisplayName && !user.Archived));
        // this.userList.set(users)
      }
    })
  }

  copyUrlToClipboard() {
    navigator.clipboard.writeText(this.binderUrl);
    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Binder URL Copied to Clipboard', life: 3000 });
  }

  getPerms(binderAccess: BinderAccess) {
    if (!binderAccess.ViewPermission && !binderAccess.EditPermission && !binderAccess.SharePermission) {
      return 'No Access';
    }
    let perms = [];
    if (binderAccess.ViewPermission) {
      perms.push('View');
    }
    if (binderAccess.EditPermission) {
      perms.push('Edit');
    }
    if (binderAccess.SharePermission) {
      perms.push('Share');
    }
    return perms.join(', ');
  }

  getAccessOptions(user: BinderAccess) {
    if (user.ViewPermission && user.EditPermission && user.SharePermission) {
      return ['View', 'Edit'];
    }
    if (user.ViewPermission && user.EditPermission) {
      return ['View', 'Edit'];
    }
    return ['View'];
  }

  get shared() {
    return this.shareService.sharedData().filter((member) => member.Users);
  }

  get teamMembers() {
    return this.shareService.teamData();
  }

  shareUnlocked() {
    const user = this.appAuthService.userData();
    const binderEditAccess = this.appAuthService.checkForPerm('Binders', 'edit', this.currentBinderService.currentBinder().SelectedFID);
    if (binderEditAccess) {
      return true;
    }
    if (user) {
      const sharedAccess = this.shareService.sharedData().some((member) => member.UID === user.UID && (member.EditPermission || member.SharePermission));
      if (sharedAccess) {
        return true;
      }
    }
    return false;
  }

  getUserImage(user: BinderAccess | UserData | undefined) {
    if (user && user.UID) {
      const userList = this.usersService.userList();
      const userRecord = userList?.find((u: UserData) => u.UID === user?.UID);
      if (userRecord && userRecord.profilePicture) {
        return 'data:image/jpeg;base64,' + userRecord.profilePicture;
      }
    }
    return null;

  }

  getInitials(displayName: string) {
    const divided = displayName.split(' ')
    if (!divided) return 'N/A';
    const firstName = divided[0] || '';
    const lastName = divided[1] || '';
    return (firstName[0] || '') + (lastName[0] || '');
  }

  onInvite() {
    this.shareService.shareLoading.set(true);
    if (this.shareUser) {
      const accessRecord = {
        Binders: {
          connect: {
            BinderID: this.currentBinderService.currentBinder().BinderID
          }
        },
        Users: {
          connect: {
            UID: this.shareUser.UID
          }
        },
        ViewPermission: true,
        EditPermission: this.selectedAccess === 'Edit Access'
      }
      const body = {
        access: accessRecord,
        binderName: this.currentBinderService.currentBinder().ClientLastName,
        binderId: this.currentBinderService.currentBinder().BinderID,
        userName: this.shareUser.DisplayName,
        userEmail: this.shareUser.User
      }
      this.shareService.addAccess(body);
    }
  }

  removeMember(member: BinderAccess) {
    this.shareService.removeAccess(member)
  }

  confirmRemove(member: BinderAccess) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to remove this user from the binder?',
      accept: () => {
        this.shareService.removeAccess(member);
      }
    });
  }

  // getUserProfileImage(UID: number) {
  //   return this.usersService.getUserProfileImage(UID)
  // }
}